/**
 * CSV Generation for Tariff Calculation Data
 *
 * @description Generates full-year hourly calculation CSV with all 6 tariffs
 * @version 1.0.0
 * @created 2025-10-27
 * @author eCompanion2 CSV Export Feature
 *
 * Purpose: Export detailed hourly calculation results for user analysis
 * in Excel or other spreadsheet applications.
 *
 * Features:
 * - Full year of hourly data (8760 or 8784 rows)
 * - All 6 tariffs: G11, G12, G12as, G12w, G13, G13s
 * - Energy and distribution costs per time zone
 * - Polish date/time formatting
 * - UTF-8 BOM for Excel compatibility
 * - Locale-safe number formatting
 */

import { TAURON_2025_PRICING, VAT_RATE } from '../../../../amplify/data/utils/tariff-calculator';
import { isPolishHoliday } from '../holidays';
import { calculateHourlyConsumption, type HourlyConsumptionData } from '../calculator/calculateHourlyConsumption';

/**
 * Tariff Type
 */
export type TariffType = 'G11' | 'G12' | 'G12as' | 'G12w' | 'G13' | 'G13s';

/**
 * Hourly Config Pattern
 */
export interface HourlyConfig {
  weekday: number[];
  weekend: number[];
}

/**
 * Saved Calculation Data (input for CSV generation)
 */
export interface SavedCalculationData {
  year: number;
  monthlyConsumption: number[];
  hourlyConfig: HourlyConfig;
}

/**
 * Time Zone Assignment for Each Tariff
 */
interface TariffZone {
  tariffCode: string;
  zoneName: string;
  energyCost: number;
  distributionCost: number;
}

/**
 * G12 Day Hours (06:00-22:00)
 */
const G12_DAY_START = 6;
const G12_DAY_END = 22;

/**
 * Format number with 2 decimal places (for consumption)
 *
 * @param value - Number to format
 * @returns Formatted string with dot decimal separator
 */
function formatConsumption(value: number): string {
  return value.toFixed(2);
}

/**
 * Format number with 4 decimal places (for costs)
 *
 * @param value - Number to format
 * @returns Formatted string with dot decimal separator
 */
function formatCost(value: number): string {
  return value.toFixed(4);
}

/**
 * Format date as YYYY-MM-DD
 *
 * @param date - Date to format
 * @returns ISO date string (YYYY-MM-DD)
 */
function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format hour as HH:00
 *
 * @param hour - Hour (0-23)
 * @returns Formatted hour string
 */
function formatHour(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

/**
 * Get day type label in Polish
 *
 * @param dayType - Day type classification
 * @returns Polish label
 */
function getDayTypeLabel(dayType: 'weekday' | 'weekend' | 'holiday'): string {
  switch (dayType) {
    case 'weekday':
      return 'Dzień roboczy';
    case 'weekend':
      return 'Weekend';
    case 'holiday':
      return 'Dzień roboczy'; // Holidays are weekdays but with holiday flag
    default:
      return 'Dzień roboczy';
  }
}

/**
 * Determine tariff zones and costs for a given hour
 *
 * Returns array of zones with energy and distribution costs (net prices, PLN).
 *
 * @param hour - Hour of day (0-23)
 * @param dayType - Day type classification
 * @param isHoliday - Is this a holiday?
 * @param consumption - Consumption for this hour (kWh)
 * @returns Array of tariff zones with costs
 */
function calculateTariffZones(
  hour: number,
  dayType: 'weekday' | 'weekend' | 'holiday',
  isHoliday: boolean,
  consumption: number
): TariffZone[] {
  const zones: TariffZone[] = [];
  const pricing = TAURON_2025_PRICING;

  // Helper: Is weekend or holiday (for tariff logic)
  const isWeekendOrHoliday = dayType === 'weekend' || isHoliday;

  // G11 - Całodobowa (single zone)
  zones.push({
    tariffCode: 'G11',
    zoneName: 'Całodobowa',
    energyCost: consumption * (pricing.g11.energyPricePerKwh + (pricing.g11.qualityCharge || 0)) * VAT_RATE,
    distributionCost: consumption * pricing.g11.distributionPerKwh * VAT_RATE
  });

  // G12 - Day (06:00-22:00) / Night (22:00-06:00)
  const isG12Day = hour >= G12_DAY_START && hour < G12_DAY_END;
  if (isG12Day) {
    zones.push({
      tariffCode: 'G12',
      zoneName: 'Dzień',
      energyCost: consumption * (pricing.g12.energyDayPricePerKwh + (pricing.g12.qualityCharge || 0)) * VAT_RATE,
      distributionCost: consumption * pricing.g12.distributionDayPerKwh * VAT_RATE
    });
  } else {
    zones.push({
      tariffCode: 'G12',
      zoneName: 'Noc',
      energyCost: consumption * (pricing.g12.energyNightPricePerKwh + (pricing.g12.qualityCharge || 0)) * VAT_RATE,
      distributionCost: consumption * pricing.g12.distributionNightPerKwh * VAT_RATE
    });
  }

  // G12as - Day (06:00-22:00, all days) / Night (22:00-06:00, all days)
  if (pricing.g12as) {
    const isG12asDay = hour >= G12_DAY_START && hour < G12_DAY_END;
    if (isG12asDay) {
      zones.push({
        tariffCode: 'G12as',
        zoneName: 'Dzień',
        energyCost: consumption * (pricing.g12as.energyPricePerKwh + (pricing.g12as.qualityCharge || 0)) * VAT_RATE,
        distributionCost: consumption * pricing.g12as.distributionDayPerKwh * VAT_RATE
      });
    } else {
      zones.push({
        tariffCode: 'G12as',
        zoneName: 'Noc',
        energyCost: consumption * (pricing.g12as.energyPricePerKwh + (pricing.g12as.qualityCharge || 0)) * VAT_RATE,
        distributionCost: consumption * pricing.g12as.distributionNightPerKwh * VAT_RATE
      });
    }
  }

  // G12w - Weekday Day / Weekday Night / Weekend
  if (pricing.g12w) {
    if (isWeekendOrHoliday) {
      // Weekend: all hours use weekend rate
      zones.push({
        tariffCode: 'G12w',
        zoneName: 'Weekend',
        energyCost: consumption * (pricing.g12w.energyPricePerKwh + (pricing.g12w.qualityCharge || 0)) * VAT_RATE,
        distributionCost: consumption * pricing.g12w.distributionWeekendPerKwh * VAT_RATE
      });
    } else {
      // Weekday: split by time
      const isG12wDay = hour >= G12_DAY_START && hour < G12_DAY_END;
      if (isG12wDay) {
        zones.push({
          tariffCode: 'G12w',
          zoneName: 'Dzień powszedni',
          energyCost: consumption * (pricing.g12w.energyPricePerKwh + (pricing.g12w.qualityCharge || 0)) * VAT_RATE,
          distributionCost: consumption * pricing.g12w.distributionDayPerKwh * VAT_RATE
        });
      } else {
        zones.push({
          tariffCode: 'G12w',
          zoneName: 'Noc powszednia',
          energyCost: consumption * (pricing.g12w.energyPricePerKwh + (pricing.g12w.qualityCharge || 0)) * VAT_RATE,
          distributionCost: consumption * pricing.g12w.distributionNightPerKwh * VAT_RATE
        });
      }
    }
  }

  // G13 - Peak / Off-peak
  if (pricing.g13) {
    if (isWeekendOrHoliday) {
      // Weekends/holidays: all hours off-peak
      zones.push({
        tariffCode: 'G13',
        zoneName: 'Pozaszczyt',
        energyCost: consumption * (pricing.g13.energyPricePerKwh + (pricing.g13.qualityCharge || 0)) * VAT_RATE,
        distributionCost: consumption * pricing.g13.distributionOffPeakPerKwh * VAT_RATE
      });
    } else {
      // Weekdays: peak hours (7-13, 16-21 winter / 19-22 summer)
      // Simplified: use winter hours (16-21) for all months
      //
      // PHASE 2 ENHANCEMENT: Add seasonal variation for G13 peak hours
      // - Winter (Oct-Mar): Evening peak 16-21
      // - Summer (Apr-Sep): Evening peak 19-22
      // Phase 1 MVP: Winter hours used year-round (conservative pricing estimate)
      const isMorningPeak = hour >= 7 && hour < 13;
      const isEveningPeak = hour >= 16 && hour < 21;

      if (isMorningPeak || isEveningPeak) {
        zones.push({
          tariffCode: 'G13',
          zoneName: 'Szczyt',
          energyCost: consumption * (pricing.g13.energyPricePerKwh + (pricing.g13.qualityCharge || 0)) * VAT_RATE,
          distributionCost: consumption * pricing.g13.distributionPeakPerKwh * VAT_RATE
        });
      } else {
        zones.push({
          tariffCode: 'G13',
          zoneName: 'Pozaszczyt',
          energyCost: consumption * (pricing.g13.energyPricePerKwh + (pricing.g13.qualityCharge || 0)) * VAT_RATE,
          distributionCost: consumption * pricing.g13.distributionOffPeakPerKwh * VAT_RATE
        });
      }
    }
  }

  // G13s - Peak / Solar Off-peak / Night
  if (pricing.g13s) {
    if (isWeekendOrHoliday) {
      // Weekends/holidays: all hours off-peak
      zones.push({
        tariffCode: 'G13s',
        zoneName: 'Pozaszczyt solar',
        energyCost: consumption * (pricing.g13s.energyPricePerKwh + (pricing.g13s.qualityCharge || 0)) * VAT_RATE,
        distributionCost: consumption * pricing.g13s.distributionOffPeakPerKwh * VAT_RATE
      });
    } else {
      // Weekdays: solar hours (9-17 summer, 10-15 winter)
      // Simplified: use winter hours (10-15) for all months
      //
      // PHASE 2 ENHANCEMENT: Add seasonal variation for G13s solar off-peak hours
      // - Winter (Oct-Mar): Solar off-peak 10-15
      // - Summer (Apr-Sep): Solar off-peak 9-17
      // Phase 1 MVP: Winter hours used year-round (conservative pricing estimate)
      const isSolarOffPeak = hour >= 10 && hour < 15;
      const isNight = hour >= 21 || hour < 7;

      if (isSolarOffPeak || isNight) {
        zones.push({
          tariffCode: 'G13s',
          zoneName: 'Pozaszczyt solar',
          energyCost: consumption * (pricing.g13s.energyPricePerKwh + (pricing.g13s.qualityCharge || 0)) * VAT_RATE,
          distributionCost: consumption * pricing.g13s.distributionOffPeakPerKwh * VAT_RATE
        });
      } else {
        zones.push({
          tariffCode: 'G13s',
          zoneName: 'Szczyt',
          energyCost: consumption * (pricing.g13s.energyPricePerKwh + (pricing.g13s.qualityCharge || 0)) * VAT_RATE,
          distributionCost: consumption * pricing.g13s.distributionPeakPerKwh * VAT_RATE
        });
      }
    }
  }

  return zones;
}

/**
 * Generate CSV header row
 *
 * @returns CSV header row
 */
function generateCSVHeader(): string {
  const columns = [
    'Data',
    'Godzina',
    'Rodzaj dnia',
    'Święto',
    'Zużycie (kWh)',
    // G11
    'G11 - Całodobowa - Energia (zł)',
    'G11 - Całodobowa - Dystrybucja (zł)',
    // G12
    'G12 - Strefa - Energia (zł)',
    'G12 - Strefa - Dystrybucja (zł)',
    // G12as
    'G12as - Strefa - Energia (zł)',
    'G12as - Strefa - Dystrybucja (zł)',
    // G12w
    'G12w - Strefa - Energia (zł)',
    'G12w - Strefa - Dystrybucja (zł)',
    // G13
    'G13 - Strefa - Energia (zł)',
    'G13 - Strefa - Dystrybucja (zł)',
    // G13s
    'G13s - Strefa - Energia (zł)',
    'G13s - Strefa - Dystrybucja (zł)'
  ];

  return columns.join(',');
}

/**
 * Generate CSV data row for a single hour
 *
 * @param hourData - Hourly consumption data point
 * @returns CSV data row
 */
function generateCSVRow(hourData: HourlyConsumptionData): string {
  const { date, hour, consumption, dayType } = hourData;

  // Basic columns
  const dateStr = formatDate(date);
  const hourStr = formatHour(hour);
  const dayTypeLabel = getDayTypeLabel(dayType);
  const isHoliday = isPolishHoliday(date) ? '1' : '0';
  const consumptionStr = formatConsumption(consumption);

  // Calculate tariff zones
  const zones = calculateTariffZones(hour, dayType, isHoliday === '1', consumption);

  // Find zone costs for each tariff
  const g11Zone = zones.find(z => z.tariffCode === 'G11');
  const g12Zone = zones.find(z => z.tariffCode === 'G12');
  const g12asZone = zones.find(z => z.tariffCode === 'G12as');
  const g12wZone = zones.find(z => z.tariffCode === 'G12w');
  const g13Zone = zones.find(z => z.tariffCode === 'G13');
  const g13sZone = zones.find(z => z.tariffCode === 'G13s');

  // Build row data
  const rowData = [
    dateStr,
    hourStr,
    dayTypeLabel,
    isHoliday,
    consumptionStr,
    // G11
    formatCost(g11Zone?.energyCost || 0),
    formatCost(g11Zone?.distributionCost || 0),
    // G12
    formatCost(g12Zone?.energyCost || 0),
    formatCost(g12Zone?.distributionCost || 0),
    // G12as
    formatCost(g12asZone?.energyCost || 0),
    formatCost(g12asZone?.distributionCost || 0),
    // G12w
    formatCost(g12wZone?.energyCost || 0),
    formatCost(g12wZone?.distributionCost || 0),
    // G13
    formatCost(g13Zone?.energyCost || 0),
    formatCost(g13Zone?.distributionCost || 0),
    // G13s
    formatCost(g13sZone?.energyCost || 0),
    formatCost(g13sZone?.distributionCost || 0)
  ];

  return rowData.join(',');
}

/**
 * Generate complete CSV file content
 *
 * Creates full-year CSV with hourly data for all 6 tariffs.
 * Includes UTF-8 BOM for Excel compatibility.
 *
 * @param calculationData - Saved calculation data
 * @returns CSV file content as string
 *
 * @example
 * const csv = generateCalculationCSV({
 *   year: 2025,
 *   monthlyConsumption: [300, 280, 260, ...],
 *   hourlyConfig: {
 *     weekday: [0.5, 0.5, ...],
 *     weekend: [0.8, 0.7, ...]
 *   }
 * });
 */
export function generateCalculationCSV(calculationData: SavedCalculationData): string {
  // Calculate hourly consumption for the entire year
  const hourlyData = calculateHourlyConsumption(
    calculationData.monthlyConsumption,
    calculationData.hourlyConfig,
    calculationData.year
  );

  // Start with UTF-8 BOM for Excel compatibility
  const BOM = '\uFEFF';

  // Generate header
  const header = generateCSVHeader();

  // Generate data rows
  const rows = hourlyData.map((hourData: HourlyConsumptionData) => generateCSVRow(hourData));

  // Combine all parts
  const csvContent = BOM + header + '\n' + rows.join('\n');

  return csvContent;
}

/**
 * Default export
 */
export default {
  generateCalculationCSV
};
